import React, { useState, useRef, useEffect } from "react";
import { FiVolume2, FiVolumeX, FiPause, FiPlay } from "react-icons/fi";
import { useVideo } from "../contexts/VideoContext";

const VideoControls = () => {
  const { isMuted, isPaused, volume, toggleSound, setVolumeLevel, togglePause } = useVideo();
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const hideTimerRef = useRef(null);

  // Détection mobile - masque le contrôleur de volume sur mobile
  useEffect(() => {
    const checkIfMobile = () => {
      // Détection par User Agent
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      
      // Détection par taille d'écran
      const isSmallScreen = window.innerWidth <= 768;
      
      // Détection par capacités tactiles
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      setIsMobile(isMobileDevice || isSmallScreen || hasTouchScreen);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolumeLevel(newVolume);
  };

  const calculateVolumeFromPosition = (clientY) => {
    if (sliderRef.current) {
      const rect = sliderRef.current.getBoundingClientRect();
      const height = rect.height;
      const clickY = clientY - rect.top;
      const percentage = 1 - (clickY / height); // Inversé pour que le haut = volume max
      return Math.max(0, Math.min(1, percentage));
    }
    return 0;
  };

  const handleSliderClick = (e) => {
    const newVolume = calculateVolumeFromPosition(e.clientY);
    setVolumeLevel(newVolume);
    resetHideTimer();
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const newVolume = calculateVolumeFromPosition(e.clientY);
    setVolumeLevel(newVolume);
    resetHideTimer();
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newVolume = calculateVolumeFromPosition(e.clientY);
      setVolumeLevel(newVolume);
      resetHideTimer();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    resetHideTimer();
  };

  const resetHideTimer = () => {
    // Nettoyer le timer existant
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    
    // Créer un nouveau timer pour masquer après 1 seconde
    hideTimerRef.current = setTimeout(() => {
      if (!isDragging) {
        setShowVolumeSlider(false);
      }
    }, 1000);
  };

  const handleMouseEnter = () => {
    setShowVolumeSlider(true);
    resetHideTimer();
  };

  const handleMouseLeave = () => {
    if (!isDragging) {
      resetHideTimer();
    }
  };

  const handleClickOutside = (e) => {
    // Vérifier si le clic est en dehors du contrôleur de volume
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setShowVolumeSlider(false);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Ajouter l'écouteur de clic global quand le contrôleur est visible
  useEffect(() => {
    if (showVolumeSlider) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showVolumeSlider]);

  // Nettoyer le timer quand le composant se démonte
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return <FiVolumeX size={18} />;
    if (volume < 0.3) return <FiVolume2 size={18} />;
    return <FiVolume2 size={18} />;
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 1000,
        display: "flex",
        gap: "10px",
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(20px)",
        padding: "12px",
        borderRadius: "15px",
        border: "1px solid rgba(212, 175, 55, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)"
      }}
    >
      {/* Contrôleur de volume - bouton toujours visible, slider masqué sur mobile */}
      <div
        ref={containerRef}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center"
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          onClick={toggleSound}
          style={{
            background: "rgba(212, 175, 55, 0.2)",
            border: "1px solid rgba(212, 175, 55, 0.3)",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#2c1810",
            transition: "all 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "rgba(212, 175, 55, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "rgba(212, 175, 55, 0.2)";
          }}
        >
          {getVolumeIcon()}
        </button>

        {/* Volume Slider Vertical - masqué sur mobile */}
        {showVolumeSlider && !isMobile && (
            <div
              style={{
                position: "absolute",
                bottom: "50px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(212, 175, 55, 0.3)",
                borderRadius: "15px",
                padding: "15px 8px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                zIndex: 1001,
                animation: "slideInVolume 0.3s ease-out"
              }}
              onMouseEnter={resetHideTimer}
              onMouseLeave={() => {
                if (!isDragging) {
                  resetHideTimer();
                }
              }}
            >
              <div
                ref={sliderRef}
                style={{
                  width: "6px",
                  height: "120px",
                  background: "rgba(212, 175, 55, 0.3)",
                  borderRadius: "3px",
                  position: "relative",
                  cursor: "pointer"
                }}
                onClick={handleSliderClick}
                onMouseDown={handleMouseDown}
              >
                {/* Volume Level Indicator */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: `${(isMuted ? 0 : volume) * 100}%`,
                    background: "#d4af37",
                    borderRadius: "3px",
                    transition: "height 0.1s ease"
                  }}
                />
                
                {/* Slider Thumb */}
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    bottom: `${(isMuted ? 0 : volume) * 100}%`,
                    transform: "translateX(-50%) translateY(50%)",
                    width: "20px",
                    height: "20px",
                    background: "#d4af37",
                    borderRadius: "50%",
                    boxShadow: "0 2px 8px rgba(212, 175, 55, 0.3)",
                    cursor: isDragging ? "grabbing" : "grab",
                    transition: "all 0.3s ease",
                    zIndex: 2
                  }}
                  onMouseDown={handleMouseDown}
                />
              </div>
              
              <style>{`
                @keyframes slideInVolume {
                  from {
                    opacity: 0;
                    transform: translateX(-50%) translateY(10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateX(-50%) translateY(0);
                  }
                }
              `}</style>
            </div>
          )}
        </div>

      <button
        onClick={togglePause}
        style={{
          background: "rgba(212, 175, 55, 0.2)",
          border: "1px solid rgba(212, 175, 55, 0.3)",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#2c1810",
          transition: "all 0.3s ease"
        }}
        onMouseEnter={(e) => {
          e.target.style.background = "rgba(212, 175, 55, 0.3)";
        }}
        onMouseLeave={(e) => {
          e.target.style.background = "rgba(212, 175, 55, 0.2)";
        }}
      >
        {isPaused ? <FiPlay size={18} /> : <FiPause size={18} />}
      </button>
    </div>
  );
};

export default VideoControls;
